'use client'

import classnames from 'classnames'
import styles from './MenuListing.module.scss'
import ContentWithStickyNavigation from '@/components/ContentWithStickyNavigation/ContentWithStickyNavigation'
import { getMenuItemListingGroupId } from '@/utils/urls'
import MenuListingListItem from '@/sections/MenuListing/MenuListingListItem/MenuListingListItem'
import Button from '@/components/Button/Button'
import useI18n from '@/hooks/use-i18n'
import Widont from '@/components/Widont/Widont'
import AnimateContainer from '@/components/AnimateContainer/AnimateContainer'

const MenuListing = ({
  className,
  itemsByCategory,
  sectionBackground,
  allergenGuidePdf,
  allergenGuideLink,
  allergenText,
  prependLineItems,
  prependContent,
}: SanityMenuListing) => {
  const { i18n } = useI18n()

  // The link wins; the legacy PDF is only a transitional fallback until every section has a link.
  const allergenGuideButtonLink: SanityLink | undefined = allergenGuideLink?.link
    ? { ...allergenGuideLink, label: allergenGuideLink.label || i18n('viewAllergenGuide') }
    : allergenGuidePdf?.asset?.url
      ? { label: i18n('viewAllergenGuide'), linkType: 'external', link: allergenGuidePdf.asset.url }
      : undefined

  return (
    <div className={classnames(styles.MenuListing, className)}>
      <div className={styles.inner}>
        <ContentWithStickyNavigation
          sectionBackground={sectionBackground}
          lineItems={[
            ...(prependLineItems ?? []),
            ...itemsByCategory.map(itemsByCategory => ({
              title: itemsByCategory.categoryReference.title,
              id: getMenuItemListingGroupId(itemsByCategory.categoryReference.slug.current),
            })),
          ]}
          className={styles.contentWithStickyNavigation}
          contentGroupQuerySelector="[data-menu-category]"
          widerNav
          bottomNavContent={
            <div className={styles.bottomNavContent}>
              <p className={styles.bottomNavContent__allergenText}>
                {<Widont text={allergenText || i18n('menuAllergenText') as string} />}
              </p>
              {allergenGuideButtonLink && (
                <Button
                  className={styles.bottomNavContent__button}
                  link={allergenGuideButtonLink}
                  icon="caretRight"
                />
              )}
            </div>
          }
        >
          {prependContent}
          <ul
            className={classnames(styles.allCategories, {
              [styles.allCategoriesWithPrepend]: prependContent,
            })}
          >
            {itemsByCategory.map((category, i) => {
              return (
                <li
                  key={i}
                  className={styles.categoryContainer}
                  data-menu-category={category.categoryReference.slug.current}
                  data-scroll-header-offset={prependContent && i === 0 ? 'true' : undefined}
                  id={getMenuItemListingGroupId(category.categoryReference.slug.current)}
                >
                  <AnimateContainer
                    element="h1"
                    animationType="fadeIn"
                    className={styles.categoryTitle}
                  >
                    {category.categoryReference.title}
                  </AnimateContainer>
                  <AnimateContainer
                    element="ul"
                    animationType="fadeIn"
                    className={styles.itemsList}
                  >
                    {category.items.map((item, _i) => {
                      return (
                        <MenuListingListItem
                          key={_i}
                          {...item}
                        />
                      )
                    })}
                  </AnimateContainer>
                </li>
              )
            })}
          </ul>
        </ContentWithStickyNavigation>
      </div>
    </div>
  )
}

MenuListing.displayName = 'MenuListing'

export default MenuListing
